<template>
  <div>
    <Tabs>
      <template #tab-selector>
        <button
          :class="{active: tabContent === TAB_REPORTS }"
          class="tab-link"
          @click="setTab(TAB_REPORTS)"
        >
          {{ $t('components.reports.tabs.reports') }}
        </button>
        <button
          :class="{active: tabContent === TAB_STATISTICS }"
          class="tab-link"
          @click="setTab(TAB_STATISTICS)"
        >
          {{ $t('components.reports.tabs.statistics') }}
        </button>
      </template>
      <template #tab-content>
        <div v-show="tabContent === TAB_REPORTS">
          <div class="row g-2 align-items-start">
            <div class="col-12 col-md-6 col-xl">
              <date-picker
                v-model="v.dateFrom.$model"
                :class="{ 'custom-error': v.dateFrom.$error, 'is-invalid': v.dateFrom.$error }"
                :placeholder="$t('components.datepicker.startPlaceholder')"
                :type="'datetime'"
                :range="false"
                :start-time="{ hours: 0, minutes: 0 }"
                :time-picker-inline="true"
                @emitDate="emitStartDate"
              />
            </div>
            <div class="col-12 col-md-6 col-xl">
              <date-picker
                v-model="v.dateTo.$model"
                :class="{ 'custom-error': v.dateTo.$error, 'is-invalid': v.dateTo.$error }"
                :placeholder="$t('components.datepicker.endPlaceholder')"
                :type="'datetime'"
                :range="false"
                :start-time="{ hours: 0, minutes: 0 }"
                :time-picker-inline="true"
                @emitDate="emitEndDate"
              />
            </div>
            <div class="col-12 col-md-6 col-xl">
              <vue-select
                v-model="v.imei.$model"
                :class="{ 'custom-error': v.imei.$error, 'is-invalid': v.imei.$error }"
                :options="vehicles"
                icon="bi bi-truck"
                label="label"
                @updateSelect="emitVehicle"
              />
            </div>
            <div class="col-12 col-md-6 col-xl">
              <vue-select
                v-model="v.reportType.$model"
                :class="{ 'custom-error': v.reportType.$error, 'is-invalid': v.reportType.$error }"
                :options="reportTypes"
                icon="bi bi-graph-up-arrow"
                label="value"
                @updateSelect="emitReportType"
              />
            </div>
            <div class="col-12 col-xl-auto">
              <button
                class="btn btn-success btn-search w-100"
                @click="search(TAB_REPORTS)"
              >
                {{ $t('components.reports.search') }}
              </button>
            </div>
          </div>
          <QuickRangeButtons @rangeSelected="applyQuickRange" />
          <div class="errors">
            <p
              v-if="v.dateFrom.$error || v.dateTo.$error || v.imei.$error || v.reportType.$error"
              class="error-msg"
            >
              {{ $t('customValidators.allMustBeCompleted') }}
            </p>
          </div>
          <div
            v-show="searched[TAB_REPORTS]"
            :key="searched[TAB_REPORTS]"
          >
            <Events
              v-if="reportType === 'events_list'"
              :date-from="dateFrom"
              :date-to="dateTo"
              :imei="imei"
              :report-type="reportType"
              :vehicle="vehicle"
            />
            <Suspense v-if="reportType === 'engine_speed'">
              <EngineSpeed
                :date-from="dateFrom"
                :date-to="dateTo"
                :imei="imei"
                :report-type="reportType"
                :vehicle="vehicle"
              />
            </Suspense>
            <Suspense v-if="reportType === 'fuel'">
              <Fuel
                :date-from="dateFrom"
                :date-to="dateTo"
                :imei="imei"
                :report-type="reportType"
                :vehicle="vehicle"
              />
            </Suspense>
            <Suspense v-if="reportType === 'voltage'">
              <Voltage
                :date-from="dateFrom"
                :date-to="dateTo"
                :imei="imei"
                :report-type="reportType"
                :vehicle="vehicle"
              />
            </Suspense>
            <SummaryView
              v-if="reportType === 'summary'"
              :date-from="dateFrom"
              :date-to="dateTo"
              :imei="imei"
              :report-type="reportType"
              :vehicle="vehicle"
            />
            <Suspense v-if="reportType === 'speed'">
              <Speed
                :date-from="dateFrom"
                :date-to="dateTo"
                :imei="imei"
                :report-type="reportType"
                :vehicle="vehicle"
              />
            </Suspense>
            <BorderCrossing
              v-if="reportType === 'border_crossing'"
              :date-from="dateFrom"
              :date-to="dateTo"
              :imei="imei"
              :report-type="reportType"
              :vehicle="vehicle"
            />
            <WeeklyReport
              v-if="reportType === 'weekly_report'"
              :date-from="dateFrom"
              :date-to="dateTo"
              :imei="imei"
              :report-type="reportType"
              :vehicle="vehicle"
            />
            <Suspense v-if="reportType === 'ignition'">
              <Ignition
                :date-from="dateFrom"
                :date-to="dateTo"
                :imei="imei"
                :report-type="reportType"
                :vehicle="vehicle"
              />
            </Suspense>
            <Geofencing
              v-if="reportType === 'geofencing'"
              :date-from="dateFrom"
              :date-to="dateTo"
              :imei="imei"
              :report-type="reportType"
              :vehicle="vehicle"
            />
          </div>
        </div>
        <div v-show="tabContent === TAB_STATISTICS">
          <div class="row g-2 align-items-start">
            <div class="col-12 col-md">
              <date-picker
                v-model="v.dateFrom.$model"
                :class="{ 'custom-error': v.dateFrom.$error, 'is-invalid': v.dateFrom.$error }"
                :placeholder="$t('components.datepicker.startPlaceholder')"
                :type="'datetime'"
                :range="false"
                :start-time="{ hours: 0, minutes: 0 }"
                :time-picker-inline="true"
                @emitDate="emitStartDate"
              />
            </div>
            <div class="col-12 col-md">
              <date-picker
                v-model="v.dateTo.$model"
                :class="{ 'custom-error': v.dateTo.$error, 'is-invalid': v.dateTo.$error }"
                :placeholder="$t('components.datepicker.endPlaceholder')"
                :type="'datetime'"
                :range="false"
                :start-time="{ hours: 0, minutes: 0 }"
                :time-picker-inline="true"
                @emitDate="emitEndDate"
              />
            </div>
            <div class="col-12 col-md-auto">
              <button
                class="btn btn-success btn-search w-100"
                @click="search(TAB_STATISTICS)"
              >
                {{ $t('components.reports.search') }}
              </button>
            </div>
          </div>
          <QuickRangeButtons @rangeSelected="applyQuickRange" />
          <div class="errors">
            <p
              v-if="v.dateFrom.$error || v.dateTo.$error"
              class="error-msg"
            >
              {{ $t('customValidators.allMustBeCompleted') }}
            </p>
          </div>
          <div
            v-if="searched[TAB_STATISTICS]"
            :key="searched[TAB_STATISTICS]"
          >
            <Statistics
              :date-from="dateFrom"
              :date-to="dateTo"
            />
          </div>
        </div>
      </template>
    </Tabs>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import DatePicker from '@/components/extras/DatePicker.vue'
import VueSelect from '@/components/extras/VueSelect.vue'
import { getListOfAllowedReports, getMyVehicles } from '@/crud/getters'
import Tabs from '@/components/extras/Tabs.vue'
import { requiredIf } from '@/vuelidate/i18n-validators'
import useVuelidate from '@vuelidate/core'
import { getHumanDate, getHumanDateTime } from '@/utils/tools'
import constants from '@/utils/constants'
import dayjs from 'dayjs'
import QuickRangeButtons from '@/components/extras/QuickRangeButtons.vue'
import Events from '@/components/content/_partials/reports/Events.vue'
import EngineSpeed from '@/components/content/_partials/reports/EngineSpeed.vue'
import Fuel from '@/components/content/_partials/reports/Fuel.vue'
import Voltage from '@/components/content/_partials/reports/Voltage.vue'
import Speed from '@/components/content/_partials/reports/Speed.vue'
import SummaryView from '@/components/content/_partials/reports/SummaryView.vue'
import BorderCrossing from '@/components/content/_partials/reports/BorderCrossing.vue'
import WeeklyReport from '@/components/content/_partials/reports/WeeklyReport.vue'
import Ignition from '@/components/content/_partials/reports/Ignition.vue'
import Geofencing from '@/components/content/_partials/reports/Geofencing.vue'
import Statistics from '@/components/content/_partials/reports/Statistics.vue'
/**
 * Tabs
 */
const TAB_REPORTS = 0
const TAB_STATISTICS = 1

export default defineComponent({
  name: 'Reports',
  components: {
    Statistics,
    SummaryView,
    Ignition,
    WeeklyReport,
    BorderCrossing,
    EngineSpeed,
    Fuel,
    Voltage,
    Events,
    Geofencing,
    DatePicker,
    VueSelect,
    Speed,
    Tabs,
    QuickRangeButtons
  },
  setup() {
    return { v: useVuelidate() }
  },
  data(): {
    TAB_REPORTS: number,
    TAB_STATISTICS: number,
    vehicles: any,
    tabContent: any,
    dateFrom: string,
    dateTo: string,
    imei: any,
    constants: any,
    vehicle: Array<any>,
    reportTypes: Array<any>
    reportType: string,
    searched: Array<number>,
  } {
    return {
      TAB_REPORTS: TAB_REPORTS,
      TAB_STATISTICS: TAB_STATISTICS,
      vehicles: [],
      tabContent: 0,
      dateFrom: '',
      dateTo: '',
      imei: null,
      constants: constants,
      vehicle: [],
      reportTypes: [],
      reportType: '',
      searched: [0, 0]
    }
  },
  methods: {
    emitStartDate(value): void {
      if (Array.isArray(value)) {
        this.dateFrom = dayjs(value[0]).format('YYYY-MM-DD HH:mm')
      } else {
        this.dateFrom = dayjs(value).format('YYYY-MM-DD HH:mm')
      }
    },
    emitEndDate(value): void {
      if (Array.isArray(value)) {
        this.dateTo = dayjs(value[0]).format('YYYY-MM-DD HH:mm')
      } else {
        this.dateTo = dayjs(value).format('YYYY-MM-DD HH:mm')
      }
    },
    applyQuickRange(range: { start: string, end: string }): void {
      this.dateFrom = range.start
      this.dateTo = range.end
    },
    emitVehicle(value): void {
      if (value) {
        this.vehicle = value
        this.imei = BigInt(value.device.imei)
        getListOfAllowedReports(this.imei)
            .then((response) => {
              const result: Array<string> = []
              response.data.forEach(obj => {
                result.push(obj.name)
              })
              this.reportTypes = [
                { key: 'events_list', value: this.$t('components.reports.types.eventsList') },
                { key: 'engine_speed', value: this.$t('components.reports.types.engineSpeed') },
                { key: 'fuel', value: this.$t('components.reports.types.fuel') },
                { key: 'voltage', value: this.$t('components.reports.types.voltage') },
                { key: 'summary', value: this.$t('components.reports.types.summary') },
                { key: 'speed', value: this.$t('components.reports.types.speed') },
                { key: 'border_crossing', value: this.$t('components.reports.types.borderCrossing') },
                { key: 'weekly_report', value: this.$t('components.reports.types.weeklyReport') },
                { key: 'ignition', value: this.$t('components.reports.types.ignition') },
                { key: 'geofencing', value: this.$t('components.reports.types.geofencing') }
              ]
              // Filter out report types not supported by the backend
              this.reportTypes = this.reportTypes.filter((v) => {
                return result.indexOf(v.key) !== -1
              })
            })
            .catch(() => {
              this.reportTypes = []
            })
      } else {
        this.vehicle = []
        this.imei = null
      }
    },
    emitReportType(value): void {
      if (value) {
        this.reportType = value.key
        this.searched[TAB_REPORTS] = 0
      } else {
        this.reportType = ''
      }
    },
    getHumanDateTime: getHumanDateTime,
    getHumanDate: getHumanDate,
    setTab(tab: number): void {
      this.tabContent = tab
    },
    async search(tab: number): Promise<void> {
      const result = await this.v.$validate()
      if (result) {
        this.searched[tab]++
      }
    },
    getVehicleName(obj): string {
      return obj.vars.name == null || obj.vars.name === '' ? obj.registration : `${ obj.vars.name } [${ obj.registration }]`
    }
  },
  mounted() {
    const self = this
    getMyVehicles()
        .then((response: any) => {
          while (this.vehicles.length > 0) {
            this.vehicles.pop()
          }
          response.data.forEach(function (obj) {
            obj.label = self.getVehicleName(obj)
            self.vehicles.push(obj)
          })
        })
  },
  validations() {
    return {
      dateFrom: {
        required: requiredIf(() => {
          return this.tabContent === this.TAB_REPORTS
              || this.tabContent === this.TAB_STATISTICS
        })
      },
      dateTo: {
        required: requiredIf(() => {
          return this.tabContent === this.TAB_REPORTS
              || this.tabContent === this.TAB_STATISTICS
        })
      },
      imei: {
        required: requiredIf(() => {
          return this.tabContent === this.TAB_REPORTS
        })
      },
      reportType: {
        required: requiredIf(() => {
          return this.tabContent === this.TAB_REPORTS
        })
      }
    }
  }
})
</script>
<style lang="scss" scoped src="@/assets/scss/reports.scss"></style>